import { endOfDay, isAfter, startOfDay } from 'date-fns';

import { Response } from '@custom-types/Response';
import { CheckAction, CheckEntity } from '@pages/Check/Enums';
import apiFn from '@services/api-service';

interface Group {
  id: string;
  name: string;
}

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  groups: Group[];
}

export interface Check {
  id: string;
  action: CheckAction;
  entityType: CheckEntity;
  entityId: string;
  checkinAt: string;
  checkedInByUserId?: string;
  deviceName?: string;
  person: Person;
}

type CheckInsType = {
  value: {
    checkins: number;
    checkouts: number;
    uniquePeopleCheckins: number;
    uniquePeopleCheckouts: number;
  };
}[];

type PeopleCountType = {
  count: number;
};

export type Payload = {
  person: {
    id?: string;
    checkinCode?: string;
  };
  deviceName?: string;
  action: CheckAction;
  entityType: CheckEntity;
  entityId: string;
  checkinAt?: string;
};

const api = (eventId: string) => apiFn(`/api/uapi/events/${eventId}/checkin`);
const activateKioskApi = (eventId: string) =>
  apiFn(`/api/uapi/events/${eventId}/onsite-app/kiosk/activate`);
const checkInsCountApi = (eventId: string) =>
  apiFn(`/api/uapi/events/${eventId}/analytics/checkins/count/`);

const totalPeopleCountApi = (eventId: string) =>
  apiFn(`/api/uapi/events/${eventId}/onsite-app/people/count`, {
    Accept: 'application/vnd.eventmobi+json; version=p.7',
  });

export const create = (eventId: string, payload: Payload) =>
  api(eventId)
    .post('', { ...payload })
    .then((response: Response<Check>) => response.data.data);

export const activateKiosk = (eventId: string) =>
  activateKioskApi(eventId)
    .post('')
    .then(response => response.data.data);

export const getCheckIns = async (
  eventId: string,
  entityType?: CheckEntity,
  sessionId?: string
) => {
  const startDatetime = startOfDay(new Date());
  const endDatetime = endOfDay(new Date());

  const params = {
    startDatetime,
    endDatetime,
    entityType,
    entityId: sessionId,
  };

  const res = await checkInsCountApi(eventId)
    .get('', { params })
    .then((response: Response<CheckInsType>) => response.data.data);

  return res;
};

export const getTotalPeopleCount = async (eventId: string) => {
  const res = await totalPeopleCountApi(eventId)
    .get('')
    .then((response: Response<PeopleCountType>) => response.data.data.count ?? 0);

  return res;
};

export const getLatestCheck = (checks: Check[], filter?: CheckAction) => {
  if (checks.length === 0) {
    return null;
  }

  return checks
    .filter(check => (filter ? check.action === filter : check))
    .reduce((latest, next) => {
      if (latest) {
        return isAfter(new Date(next.checkinAt), new Date(latest.checkinAt)) ? next : latest;
      }

      return next;
    }, null as Check | null);
};
